import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import DefaultTermsFields from './DefaultTermsFields';
import './LoanDetailsStep.css';
import { useCalculateLoan } from '../../../services/loanService';
import { LoanCalculationRequest, LoanCalculationResult, LoanType, LoanSecurityType, PaymentScheduleItem } from '../../../services/schemas/loanServiceSchemas';
import { toast } from 'react-toastify';
import { maxLoanAmountFor } from '../../../constants/loanLimits';
import { toLocalDateString } from '../../../utils/localDate';

// Add new type for loan calculation mode
type LoanCalculationMode = 'AUTOMATIC' | 'CUSTOM';

// Add new type for custom loan type
type CustomLoanType = 'ONCE_OFF' | 'INSTALLMENTS';

// Update the form type to match the validation requirements
type LoanDetailsForm = {
  loan: {
    calculationMode?: LoanCalculationMode;
    loanProductType?: 'STANDARD' | 'TIRHELO' | 'SEDFA';
    customLoanType?: CustomLoanType;
    loanAmount: number;
    loanTerm: number;
    loanTermMonths?: number;
    loanPurpose: string;
    securityType: LoanSecurityType;
    dueDate?: string;
    interestRate?: number;
    adminFee?: number;
    totalRepayment?: number;
    interestAmount?: number;
    paymentDates?: string[];
    commencementDate?: string;
    numberOfInstallments?: number;
  };
  loanType: 'PERSONAL' | 'BUSINESS';
  collaterals?: Array<{
    assetDescription: string;
    make?: string;
    yearModel?: string;
    registrationNumber?: string;
    otherDetails?: string;
    ownerName: string;
    ownerId: string;
    value: number;
  }>;
}

const parseDateInput = (dateString?: string): Date | null => {
  if (!dateString) return null;

  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const calculateInstallmentDueDate = (startDate?: string, installments?: number): string => {
  const parsedStartDate = parseDateInput(startDate);

  if (!parsedStartDate || !installments || installments < 1) {
    return '';
  }

  const targetMonthIndex = parsedStartDate.getMonth() + installments;
  const lastDayOfTargetMonth = new Date(parsedStartDate.getFullYear(), targetMonthIndex + 1, 0).getDate();
  const dueDate = new Date(
    parsedStartDate.getFullYear(),
    targetMonthIndex,
    Math.min(parsedStartDate.getDate(), lastDayOfTargetMonth)
  );

  return formatDateInput(dueDate);
};

export default function LoanDetailsStep() {
  const { register, formState: { errors }, watch, setValue, trigger, clearErrors, setError } = useFormContext<LoanDetailsForm>();
  const loanErrors = errors.loan || {};
  const [isNextClicked, setIsNextClicked] = useState(false);
  // Track whether the user has manually chosen the loan structure so we stop auto-defaulting it
  const [structureManuallySelected, setStructureManuallySelected] = useState(false);

  // Get watched values
  const loanAmount = watch('loan.loanAmount') || 0;
  const securityType = watch('loan.securityType');
  const applicationLoanType = watch('loanType');
  const dueDateString = watch('loan.dueDate');
  const calculationMode = watch('loan.calculationMode') || 'CUSTOM';
  const selectedLoanProductType = watch('loan.loanProductType');
  const loanProductType = selectedLoanProductType || '';
  const commencementDate = watch('loan.commencementDate');
  const interestRate = watch('loan.interestRate') || 0;
  const numberOfInstallments = watch('loan.numberOfInstallments') || 1;
  const customLoanType = watch('loan.customLoanType');
  const isSedfaLoan = selectedLoanProductType === 'SEDFA';
  const isTirheloLoan = selectedLoanProductType === 'TIRHELO';
  const maxLoanAmount = maxLoanAmountFor(isSedfaLoan);
  const loanPurpose = watch('loan.loanPurpose') || '';
  const adminFee = watch('loan.adminFee');
  const fundingType = (watch('loan.fundingType' as any) as string) || '';
  const showProjectInfo = fundingType === 'PURCHASE_ORDER_FINANCE' || fundingType === 'PROJECT_FINANCE';
  const loanDescription =
    selectedLoanProductType === 'SEDFA'
      ? 'SEDFA Loan with a maximum loan amount of R250,000.'
      : selectedLoanProductType === 'TIRHELO'
        ? 'Tirhelo Loan with customizable interest rates and payment terms.'
        : 'Choose a loan structure to view the available terms.';

  useEffect(() => {
    if (!selectedLoanProductType && calculationMode === 'CUSTOM') {
      setValue('loan.calculationMode', undefined);
    }
  }, [selectedLoanProductType, calculationMode, setValue]);

  // Default the Loan Structure based on the requested loan amount:
  //  - R250,000 or less     -> SEDFA Loan (fits SEDFA's R250,000 cap)
  //  - greater than R250,000 -> Tirhelo Loan (uncapped)
  // Only applies until the user manually picks a structure themselves.
  useEffect(() => {
    if (structureManuallySelected) return;
    if (!loanAmount || loanAmount <= 0) return;

    const defaultProductType = loanAmount <= 250000 ? 'SEDFA' : 'TIRHELO';
    if (selectedLoanProductType !== defaultProductType) {
      setValue('loan.loanProductType', defaultProductType);
      setValue('loan.calculationMode', 'CUSTOM');
    }
  }, [loanAmount, structureManuallySelected, selectedLoanProductType, setValue]);

  // State variables for API response data
  const [calculationResult, setCalculationResult] = useState<LoanCalculationResult | null>(null);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentScheduleItem[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [loanTermDays, setLoanTermDays] = useState(0);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isDateRangeInvalid, setIsDateRangeInvalid] = useState(false);

  // State to track if loan fields are all valid (independent of other steps)
  const [areLoanFieldsValid, setAreLoanFieldsValid] = useState(true);
  // Track if form has been touched by user
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // Add state to track if validation was explicitly triggered (e.g., by clicking Next)
  const [validationTriggered, setValidationTriggered] = useState(false);

  // Clear errors from other steps when this step is shown
  useEffect(() => {
    // Find all error keys that aren't related to the current step
    const otherStepErrorKeys = Object.keys(errors).filter(key => key !== 'loan' && key !== 'loanType');

    if (otherStepErrorKeys.length > 0) {
      // Clear each error key that's not related to this step
      otherStepErrorKeys.forEach(key => {
        // Use type assertion to bypass TypeScript checking
        (clearErrors as any)(key);
      });
    }

    // Reset validation triggered state when component mounts
    setValidationTriggered(false);
  }, [clearErrors, errors]);

  useEffect(() => {
    if (loanAmount > 0) {
      void trigger('loan.loanAmount');
    }
  }, [loanAmount, maxLoanAmount, trigger]);

  // Helper function to determine if we should show error
  const shouldShowError = (fieldName: string): boolean => {
    const fieldWasTouched = Boolean(errors.loan && (errors.loan as any)[fieldName]);
    return isNextClicked || fieldWasTouched;
  };

  // Helper function to get error message safely
  const getErrorMessage = (fieldName: string): string => {
    const error = (errors.loan as any)?.[fieldName];
    if (!error) return '';

    // Handle different error types
    if (error.type === 'invalid_type') {
      return 'This field is required';
    }
    if (error.type === 'too_small') {
      return error.message || 'Value is too small';
    }
    if (error.type === 'too_big') {
      return error.message || 'Value is too large';
    }

    return error.message || 'Invalid value';
  };

  // Validate loan fields only when next button is clicked
  const validateLoanFields = async () => {
    setIsNextClicked(true);
    setValidationTriggered(true);

    // Define the field names with proper typing
    type LoanField =
      | 'loan.loanAmount'
      | 'loan.commencementDate'
      | 'loan.loanPurpose'
      | 'loan.dueDate'
      | 'loan.securityType'
      | 'loan.customLoanType'
      | 'loan.interestRate'
      | 'loan.numberOfInstallments';

    // Validate common fields for both modes
    const commonFields: LoanField[] = [
      'loan.loanAmount',
      'loan.commencementDate',
      'loan.loanPurpose',
      'loan.securityType',
      ...(customLoanType === 'ONCE_OFF' ? ['loan.dueDate' as LoanField] : [])
    ];

    // Add mode-specific fields
    const modeSpecificFields: LoanField[] = [
      'loan.customLoanType',
      'loan.interestRate',
      ...(customLoanType === 'INSTALLMENTS' ? ['loan.numberOfInstallments' as LoanField] : [])
    ];

    // Combine all fields to validate
    const fieldsToValidate = [...commonFields, ...modeSpecificFields];

    // Trigger validation for all required fields
    const isValid = await trigger(fieldsToValidate);
    const finalValidity = isValid && !isDateRangeInvalid;
    setAreLoanFieldsValid(finalValidity);

    return finalValidity;
  };

  // Use the calculateLoan mutation
  const calculateLoanMutation = useCalculateLoan({
    mutation: {
      onSuccess: (data) => {
        if (data) {
          // Store the complete calculation result
          setCalculationResult(data);
          setHasCalculated(true); // Set the hasCalculated flag to true

          // Group the payment schedule by month and store it
          if (data.paymentSchedule && data.paymentSchedule.length > 0) {
            const monthlySchedule = groupPaymentScheduleByMonth(data.paymentSchedule);
            setPaymentSchedule(monthlySchedule);

            const calculatedDueDate =
              data.paymentSchedule[data.paymentSchedule.length - 1]?.dueDate ||
              calculateInstallmentDueDate(commencementDate, numberOfInstallments);

            if (calculatedDueDate) {
              setValue('loan.dueDate', calculatedDueDate, { shouldValidate: true });
              clearErrors('loan.dueDate');
            }
          }

          // Update form values with the calculated results, but only for display
          setValue('loan.totalRepayment', data.totalRepayment);

          toast.success('Loan details calculated successfully');
        }
        setIsCalculating(false);
      },
      onError: (error) => {
        console.error('Error calculating loan details:', error);
        toast.error('Failed to calculate loan details. Please try again.');
        setIsCalculating(false);
      }
    }
  });

  // Format date from string (YYYY-MM-DD) to a more readable format
  const formatDateFromString = (dateString: string): string => {
    const [y, m, d] = dateString.split('-').map(Number);
    const date = new Date(y, (m ?? 1) - 1, d ?? 1);
    return formatDate(date);
  };

  const formatDate = (date: Date): string =>
      new Intl.DateTimeFormat('en-ZA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'Africa/Johannesburg',
      }).format(date);

  // Calculate loan term in days for Tirhelo/SEDFA loan products
  useEffect(() => {
    if (!calculationMode) return;

    const today = new Date();
    let termDays = 0;

    if (customLoanType === 'ONCE_OFF' && dueDateString) {
      const dueDate = new Date(dueDateString);
      const diffTime = Math.abs(dueDate.getTime() - today.getTime());
      termDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } else if (customLoanType === 'INSTALLMENTS') {
      // For installments, calculate term based on number of installments
      termDays = numberOfInstallments * 30; // Approximate 30 days per month
    }

    // Always store and use the actual days calculation
    setLoanTermDays(termDays);
    setValue('loan.loanTerm', termDays);

  }, [calculationMode, customLoanType, dueDateString, numberOfInstallments, setValue]);

  // Prevent due date from being before commencement date
  useEffect(() => {
    if (!commencementDate || !dueDateString) {
      if (isDateRangeInvalid) {
        clearErrors('loan.dueDate');
        clearErrors('loan.commencementDate');
        setIsDateRangeInvalid(false);
      }
      return;
    }

    const commencement = new Date(commencementDate);
    const dueDate = new Date(dueDateString);

    if (dueDate < commencement) {
      if (!isDateRangeInvalid) {
        setIsDateRangeInvalid(true);
      }
      setError('loan.dueDate', {
        type: 'manual',
        message: 'Due date cannot be before commencement date'
      });
      setError('loan.commencementDate', {
        type: 'manual',
        message: 'Commencement date cannot be after due date'
      });
    } else if (isDateRangeInvalid) {
      clearErrors('loan.dueDate');
      clearErrors('loan.commencementDate');
      setIsDateRangeInvalid(false);
    }
  }, [commencementDate, dueDateString, clearErrors, setError, isDateRangeInvalid]);

  useEffect(() => {
    if (customLoanType !== 'INSTALLMENTS') {
      return;
    }

    const calculatedDueDate = calculateInstallmentDueDate(commencementDate, numberOfInstallments);
    setValue('loan.dueDate', calculatedDueDate, { shouldValidate: false });

    if (calculatedDueDate) {
      clearErrors('loan.dueDate');
    }
  }, [customLoanType, commencementDate, numberOfInstallments, setValue, clearErrors]);

  // Handle loan calculation
  const handleCalculateLoan = () => {
    validateLoanFields().then((isValid) => {
      if (!isValid) {
        console.log('Validation failed, errors:', errors.loan);
        toast.error('Please correct the validation errors before calculating');
        return;
      }

      if (!securityType) {
        setError('loan.securityType', { type: 'required', message: 'Security type is required' });
        toast.error('Please select a security type');
        return;
      }

      if (!isTirheloLoan) {
        if (loanAmount < 3000) {
          toast.error('Loan amount must be at least R3,000');
          return;
        }

        if (loanAmount > maxLoanAmount) {
          toast.error(`Loan amount cannot exceed R${maxLoanAmount.toLocaleString()} for ${isSedfaLoan ? 'SEDFA Loan' : 'this loan type'}`);
          return;
        }
      }

      if (loanTermDays <= 0) {
        toast.error('Please select a valid loan term');
        return;
      }

      if (isDateRangeInvalid) {
        toast.error('Please correct the commencement and due dates');
        return;
      }

      setIsCalculating(true);

      // Prepare the request payload
      const calculationRequest: LoanCalculationRequest = {
        loanAmount: loanAmount,
        loanTermDays: loanTermDays,
        securityType: securityType as LoanSecurityType,
        calculationMode: calculationMode,
        customLoanType: customLoanType,
        commencementDate: commencementDate || toLocalDateString(),
        dueDate: customLoanType === 'ONCE_OFF' ? dueDateString : undefined,
        numberOfInstallments: customLoanType === 'INSTALLMENTS' ? numberOfInstallments : undefined,
        interestRate: calculationMode === 'CUSTOM' ? interestRate : undefined,
        adminFee: adminFee && adminFee > 0 ? adminFee : 0
      };

      // Call the API
      calculateLoanMutation.mutate({ data: calculationRequest });
    });
  };

  // Update the calculation mode change handler
  const handleCalculationModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHasUserInteracted(true);
    setStructureManuallySelected(true);
    const newProductType = e.target.value as '' | 'TIRHELO' | 'SEDFA';
    const newMode: LoanCalculationMode = 'CUSTOM';

    if (!newProductType) {
      setValue('loan.loanProductType', undefined);
      setValue('loan.calculationMode', undefined);
      return;
    }

    setValue('loan.loanProductType', newProductType);
    setValue('loan.customLoanType', undefined);
    setValue('loan.dueDate', '');
    clearErrors(['loan.customLoanType', 'loan.dueDate']);

    // Set the new calculation mode
    setValue('loan.calculationMode', newMode);
    void trigger('loan.loanAmount');
  };

  // Update the custom loan type change handler
  const handleCustomLoanTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHasUserInteracted(true);
    const value = e.target.value as CustomLoanType | '';

    if (!value) {
      setValue('loan.customLoanType', undefined);
      setValue('loan.dueDate', '');
      clearErrors(['loan.customLoanType', 'loan.dueDate', 'loan.numberOfInstallments']);
      return;
    }

    const newType = value as CustomLoanType;
    setValue('loan.dueDate', '');

    // Reset fields when switching loan types
    if (newType === 'ONCE_OFF') {
      setValue('loan.numberOfInstallments', undefined);
      clearErrors('loan.numberOfInstallments');
    } else {
      clearErrors('loan.dueDate');
    }

    // Set the new custom loan type
    setValue('loan.customLoanType', newType);
  };

  // Group payment schedule by month
  // For single payment: use exact due date from backend
  // For installments: use last day of month for each payment
  const groupPaymentScheduleByMonth = (schedule: PaymentScheduleItem[]): PaymentScheduleItem[] => {
    if (!schedule || schedule.length === 0) return [];

    // If only one payment, return it as-is with the exact due date
    if (schedule.length === 1) {
      return [{
        ...schedule[0],
        period: 1
      }];
    }

    // For multiple payments (installments), group by month and use last day of month
    const monthlyPayments: { [key: string]: PaymentScheduleItem } = {};

    schedule.forEach(payment => {
      if (!payment.dueDate) return;

      // Extract year and month from the due date
      const date = new Date(payment.dueDate);
      const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;

      if (!monthlyPayments[monthKey]) {
        // Set the due date to the last day of the month
        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const formattedLastDay = toLocalDateString(lastDayOfMonth); // YYYY-MM-DD format

        // Initialize the monthly payment with the first payment in this month
        monthlyPayments[monthKey] = {
          period: Object.keys(monthlyPayments).length + 1,
          dueDate: formattedLastDay, // Use last day of month for installments
          amount: payment.amount || 0,
          principalComponent: payment.principalComponent || 0,
          interestComponent: payment.interestComponent || 0,
          remainingBalance: payment.remainingBalance
        };
      } else {
        // Add to existing monthly payment
        monthlyPayments[monthKey].amount = (monthlyPayments[monthKey].amount || 0) + (payment.amount || 0);
        monthlyPayments[monthKey].principalComponent = (monthlyPayments[monthKey].principalComponent || 0) + (payment.principalComponent || 0);
        monthlyPayments[monthKey].interestComponent = (monthlyPayments[monthKey].interestComponent || 0) + (payment.interestComponent || 0);
        monthlyPayments[monthKey].remainingBalance = payment.remainingBalance; // Use the latest remaining balance
      }
    });

    // Convert the object to an array and sort by period
    return Object.values(monthlyPayments).sort((a, b) => (a.period || 0) - (b.period || 0));
  };

  // Ensure key loan fields are registered with the form
  useEffect(() => {
    register('loan.loanProductType');
    register('loan.calculationMode');

    if (applicationLoanType === 'BUSINESS') {
      register('loan.dueDate', {
        required: 'Due date is required'
      });
    }
  }, [applicationLoanType, register]);

  // Add effect to clear collateral details when security type changes to UNSECURED
  useEffect(() => {
    if (securityType === 'UNSECURED') {
      setValue('collaterals', []);
    }
  }, [securityType, setValue]);

  return (
    <div className="form-section">
      <h6 className="form-section-title">Loan Details</h6>

      <div className="row g-3">
        {/* Loan Structure Selector */}
        <div className="col-12 mb-4">
          <div className="form-group">
            <label htmlFor="calculationMode">Loan Structure</label>
            <select
              className={`form-select ${shouldShowError('calculationMode') && loanErrors.calculationMode ? 'is-invalid' : ''}`}
              id="calculationMode"
              value={loanProductType}
              onChange={handleCalculationModeChange}
            >
              <option value="">Choose Loan Type</option>
              <option value="TIRHELO">Tirhelo Loan</option>
              <option value="SEDFA">SEDFA Loan</option>
            </select>
            {shouldShowError('calculationMode') && loanErrors.calculationMode && (
              <div className="invalid-feedback">{loanErrors.calculationMode.message as string}</div>
            )}
            <small className="form-text text-muted">{loanDescription}</small>
          </div>
        </div>

        {/* Common Fields for both modes */}
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="loanAmount">Loan Amount (R)</label>
            <input
              type="number"
              className={`form-control ${shouldShowError('loanAmount') && loanErrors.loanAmount ? 'is-invalid' : ''}`}
              id="loanAmount"
              placeholder="Enter loan amount"
              {...register('loan.loanAmount', {
                required: 'Loan amount is required',
                valueAsNumber: true,
                validate: (value) => {
                  if (value === undefined || value === null) return 'Loan amount is required';
                  if (isNaN(value)) return 'Loan amount is required';
                  if (value <= 0) return 'Loan amount must be greater than 0';
                  if (!isTirheloLoan) {
                    if (value < 3000) return 'Loan amount must be at least R3,000';
                    if (value > maxLoanAmount) return `Loan amount cannot exceed R${maxLoanAmount.toLocaleString()} for ${isSedfaLoan ? 'SEDFA Loan' : 'this loan type'}`;
                  }
                  return true;
                },
                onChange: (e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setValue('loan.loanAmount', 0);
                    setError('loan.loanAmount', { type: 'required', message: 'Loan amount is required' });
                  } else {
                    const numValue = Number(value);
                    if (!isNaN(numValue)) {
                      setValue('loan.loanAmount', numValue);
                      if (numValue > 0) clearErrors('loan.loanAmount');
                      void trigger('loan.loanAmount');
                    }
                  }
                }
              })}
            />
            {shouldShowError('loanAmount') && loanErrors.loanAmount && (
              <div className="invalid-feedback">{loanErrors.loanAmount.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="securityType">Security Type</label>
            <select
              className={`form-select ${loanErrors.securityType ? 'is-invalid' : ''}`}
              id="securityType"
              {...register('loan.securityType', { required: 'Security type is required' })}
            >
              <option value="">Select security type</option>
              <option value="SECURED">Secured Loan</option>
              <option value="UNSECURED">Unsecured Loan</option>
            </select>
            {loanErrors.securityType && (
              <div className="invalid-feedback">{loanErrors.securityType.message as string}</div>
            )}
          </div>
        </div>

        {/* Commencement Date - Common for both modes */}
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="commencementDate">Commencement Date</label>
            <input
              type="date"
              className={`form-control ${shouldShowError('commencementDate') && loanErrors.commencementDate ? 'is-invalid' : ''}`}
              id="commencementDate"
              {...register('loan.commencementDate', {
                required: 'Commencement date is required'
              })}
            />
            {shouldShowError('commencementDate') && loanErrors.commencementDate && (
              <div className="invalid-feedback">{loanErrors.commencementDate.message as string}</div>
            )}
          </div>
        </div>

        {/* Tirhelo LoanSpecific Fields */}
        {(
          <>
            <div className="col-12 col-md-6">
              <div className="form-group">
                <label htmlFor="customLoanType">Payment Type</label>
                <select
                  className={`form-select ${shouldShowError('customLoanType') && loanErrors.customLoanType ? 'is-invalid' : ''}`}
                  id="customLoanType"
                  {...register('loan.customLoanType', { required: 'Payment type is required' })}
                  onChange={handleCustomLoanTypeChange}
                >
                  <option value="">Select payment type</option>
                  <option value="ONCE_OFF">Single Payment</option>
                  <option value="INSTALLMENTS">Monthly Installments</option>
                </select>
                {shouldShowError('customLoanType') && loanErrors.customLoanType && (
                  <div className="invalid-feedback">{loanErrors.customLoanType.message as string}</div>
                )}
                <small className="form-text text-muted">
                  {customLoanType === 'ONCE_OFF'
                    ? 'Full payment will be due on the specified date.'
                    : customLoanType === 'INSTALLMENTS'
                      ? 'Loan will be repaid in equal monthly installments.'
                      : 'Choose a payment type to see details.'}
                </small>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="form-group">
                <label htmlFor="interestRate">Interest Rate (%)</label>
                <input
                  type="number"
                  className={`form-control ${shouldShowError('interestRate') && loanErrors.interestRate ? 'is-invalid' : ''}`}
                  id="interestRate"
                  placeholder="Enter interest rate"
                  {...register('loan.interestRate', {
                    required: 'Interest rate is required',
                    valueAsNumber: true,
                    validate: (value) => {
                      if (value === undefined || value === null) return 'Interest rate is required';
                      if (isNaN(value)) return 'Interest rate is required';
                      if (value < 0) return 'Interest rate must be positive';
                      if (value > 100) return 'Interest rate cannot exceed 100%';
                      return true;
                    },
                    onChange: (e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setValue('loan.interestRate', 0);
                        setError('loan.interestRate', { type: 'required', message: 'Interest rate is required' });
                      } else {
                        const numValue = Number(value);
                        if (!isNaN(numValue)) {
                          setValue('loan.interestRate', numValue);
                          if (numValue >= 0 && numValue <= 100) {
                            clearErrors('loan.interestRate');
                          }
                        }
                      }
                    }
                  })}
                />
                {shouldShowError('interestRate') && loanErrors.interestRate && (
                  <div className="invalid-feedback">{loanErrors.interestRate.message as string}</div>
                )}
              </div>
            </div>

            {customLoanType === 'ONCE_OFF' && (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="flexibleDueDate">Due Date</label>
                  <input
                    type="date"
                    className={`form-control ${shouldShowError('dueDate') && loanErrors.dueDate ? 'is-invalid' : ''}`}
                    id="flexibleDueDate"
                    {...register('loan.dueDate', {
                      required: 'Due date is required for single payment'
                    })}
                  />
                  {shouldShowError('dueDate') && loanErrors.dueDate && (
                    <div className="invalid-feedback">{loanErrors.dueDate.message as string}</div>
                  )}
                  <small className="form-text text-muted">
                    Select the date when the full payment will be due.
                  </small>
                </div>
              </div>
            )}

            {customLoanType === 'INSTALLMENTS' && (
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="numberOfInstallments">Number of Installments</label>
                  <select
                    className={`form-select ${shouldShowError('numberOfInstallments') && loanErrors.numberOfInstallments ? 'is-invalid' : ''}`}
                    id="numberOfInstallments"
                    {...register('loan.numberOfInstallments', {
                      required: 'Number of installments is required',
                      valueAsNumber: true,
                      min: {
                        value: 1,
                        message: 'Minimum 1 installment'
                      },
                      max: {
                        value: 12,
                        message: 'Maximum 12 installments'
                      }
                    })}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        setValue('loan.numberOfInstallments', value);
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {[...Array(12)].map((_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                  {shouldShowError('numberOfInstallments') && loanErrors.numberOfInstallments && (
                    <div className="invalid-feedback">{loanErrors.numberOfInstallments.message as string}</div>
                  )}
                  <small className="form-text text-muted">
                    Select the number of monthly installments (1-12).
                  </small>
                </div>
              </div>
            )}
          </>
        )}

        {/* Admin Fee Section */}
        {calculationMode === 'CUSTOM' && (
            <div className="col-12">
              <div className="form-group">
                    <div className="col-md-6">
                      <label htmlFor="adminFee">Admin Fee (R)</label>
                      <input
                          type="number"
                          className={`form-control ${shouldShowError('adminFee') && loanErrors.adminFee ? 'is-invalid' : ''}`}
                          id="adminFee"
                          placeholder="Enter admin fee"
                          {...register('loan.adminFee', {
                            valueAsNumber: true,
                            validate: (value) => {
                              if (value !== undefined && value !== null && value < 0) return 'Admin fee must be positive';
                              return true;
                            },
                            onChange: (e) => {
                              const value = e.target.value;
                              if (value === '') {
                                setValue('loan.adminFee', 0);
                              } else {
                                const numValue = Number(value);
                                if (!isNaN(numValue)) {
                                  setValue('loan.adminFee', numValue);
                                  if (numValue >= 0) {
                                    clearErrors('loan.adminFee');
                                  }
                                }
                              }
                            }
                          })}
                      />
                      {shouldShowError('adminFee') && loanErrors.adminFee && (
                          <div className="invalid-feedback">{loanErrors.adminFee.message as string}</div>
                      )}
                    </div>
              </div>
            </div>
        )}

        {/* Default terms - common for both modes */}
        <DefaultTermsFields />

        {/* Loan Purpose - Common for both modes */}
        <div className="col-12">
          <div className="form-group">
            <label htmlFor="loanPurpose">Loan Purpose</label>
            <textarea
              className={`form-control ${shouldShowError('loanPurpose') && loanErrors.loanPurpose ? 'is-invalid' : ''}`}
              id="loanPurpose"
              rows={3}
              placeholder="Explain the purpose of this loan"
              {...register('loan.loanPurpose', {
                required: 'Loan purpose is required',
                minLength: {
                  value: 20,
                  message: 'Please provide a detailed explanation (minimum 20 characters)'
                }
              })}
            ></textarea>
            {shouldShowError('loanPurpose') && loanErrors.loanPurpose && (
              <div className="invalid-feedback">{loanErrors.loanPurpose.message as string}</div>
            )}
          </div>
        </div>

        {/* Type of Funding Requested */}
        <div className="col-12">
          <div className="form-group">
            <label htmlFor="fundingType">Funding product</label>
            <select className="form-select" id="fundingType" {...register('loan.fundingType' as any)}>
              <option value="">Select funding product…</option>
              <option value="PURCHASE_ORDER_FINANCE">Purchase Order Funding</option>
              <option value="INVOICE_DISCOUNTING_FINANCE">Invoice Discounting</option>
              <option value="WORKING_CAPITAL_FINANCE">Working Capital</option>
              <option value="PROJECT_FINANCE">Project Finance</option>
              <option value="ASSET_FINANCE">Equipment / Vehicle Funding</option>
              <option value="BRIDGING_FINANCE">Bridge Funding</option>
              <option value="OPERATIONAL_COST_FINANCE">Operational Cost Finance</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </div>

        {fundingType && (
          <div className="col-12">
            <div className="alert alert-light border small mb-0">
              The details specific to this funding product are captured on the next step.
            </div>
          </div>
        )}

        {/* Calculate Button */}
        {loanAmount >= 3000 && loanAmount <= maxLoanAmount && (
          <div className="col-12 mb-3">
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCalculateLoan}
                disabled={isCalculating || isDateRangeInvalid}
              >
                {isCalculating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Calculating...
                  </>
                ) : (
                  'Calculate Loan Repayment'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Display calculation results */}
        {calculationResult && (
          <>
            <div className="col-12">
              <hr className="my-3" />
            </div>
            <div className="col-12">
              <div className="card loan-summary-card">
                <div className="card-body">
                  <h6 className="loan-summary-title">Loan Summary</h6>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="loan-summary-item">
                        <span className="label">Loan Amount:</span>
                        <span className="value">R {calculationResult.loanAmount?.toLocaleString() || loanAmount.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="loan-summary-item">
                        <span className="label">Calculation Mode:</span>
                        <span className="value">{calculationMode}</span>
                      </div>
                    </div>
                    {calculationMode === 'CUSTOM' && (
                      <>
                        <div className="col-12 col-md-6">
                          <div className="loan-summary-item">
                            <span className="label">Loan Type:</span>
                            <span className="value">
                              {customLoanType === 'ONCE_OFF'
                                ? 'Once-off Payment'
                                : customLoanType === 'INSTALLMENTS'
                                  ? 'Installment-based'
                                  : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="loan-summary-item">
                            <span className="label">Interest Rate:</span>
                            <span className="value">{interestRate}%</span>
                          </div>
                        </div>
                        {customLoanType === 'ONCE_OFF' && (
                          <div className="col-12 col-md-6">
                            <div className="loan-summary-item">
                              <span className="label">Due Date:</span>
                              <span className="value">{dueDateString ? formatDateFromString(dueDateString) : 'N/A'}</span>
                            </div>
                          </div>
                        )}
                        {customLoanType === 'INSTALLMENTS' && (
                          <div className="col-12 col-md-6">
                            <div className="loan-summary-item">
                              <span className="label">Number of Installments:</span>
                              <span className="value">{numberOfInstallments || 'N/A'}</span>
                            </div>
                          </div>
                        )}
                        {customLoanType === 'INSTALLMENTS' && dueDateString && (
                          <div className="col-12 col-md-6">
                            <div className="loan-summary-item">
                              <span className="label">Final Due Date:</span>
                              <span className="value">{formatDateFromString(dueDateString)}</span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <div className="col-12 col-md-6">
                      <div className="loan-summary-item">
                        <span className="label">Security Type:</span>
                        <span className="value">{securityType || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="loan-summary-item">
                        <span className="label">Admin Fee:</span>
                        <span className="value">R {calculationResult.adminFee?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="loan-summary-item total-repayment">
                        <span className="label">Total Repayment:</span>
                        <span className="value">R {calculationResult.totalRepayment?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Display payment schedule if available */}
        {calculationResult && paymentSchedule.length > 0 && (
          <div className="col-12 mt-4">
            <div className="card">
              <div className="card-body">
                <h6 className="loan-summary-title">Payment Schedule</h6>
                <div className="payment-schedule-grid">
                  {paymentSchedule.map((payment, index) => (
                    <div key={index} className={`payment-card ${index === paymentSchedule.length - 1 ? 'payment-card-final' : ''}`}>
                      <div className="payment-card-header">
                        <span className="payment-month">Payment {index + 1}</span>
                        <span className="payment-date">{payment.dueDate ? formatDateFromString(payment.dueDate) : 'N/A'}</span>
                      </div>
                      <div className="payment-card-body">
                        <div className="payment-amount">
                          <div className="payment-value">R {payment.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</div>
                        </div>
                        <div className="payment-breakdown">
                          <div className="payment-principal">
                            <span className="payment-label">Principal:</span>
                            <span className="payment-value">R {payment.principalComponent?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                          </div>
                          <div className="payment-interest">
                            <span className="payment-label">Interest:</span>
                            <span className="payment-value">R {payment.interestComponent?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                          </div>
                          <div className="payment-balance">
                            <span className="payment-label">Remaining:</span>
                            <span className="payment-value">R {payment.remainingBalance?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {loanAmount > 0 && !isTirheloLoan && (loanAmount < 3000 || loanAmount > maxLoanAmount) && (
          <div className="col-12">
            <div className="alert alert-warning">
              <strong>Warning:</strong> Loan amount must be between R3,000 and R{maxLoanAmount.toLocaleString()} to calculate loan details.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
